// PTO/Sick Day Settings Editor
import React, { useState } from 'react';
import ResponsiveModal from './ResponsiveModal';
import { Settings, Save } from 'lucide-react';

interface DayCounter {
  label: string;
  used: number;
  total: number;
  color: string;
}

interface PTOSettingsEditorProps {
  counters: DayCounter[];
  onSave: (counters: DayCounter[]) => void;
}

export default function PTOSettingsEditor({ counters, onSave }: PTOSettingsEditorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [editedCounters, setEditedCounters] = useState<DayCounter[]>([]);

  const handleOpen = () => {
    setEditedCounters([...counters]);
    setIsOpen(true);
  };

  const handleSave = () => {
    onSave(editedCounters);
    setIsOpen(false);
  };

  const updateCounter = (index: number, field: keyof DayCounter, value: string | number) => {
    setEditedCounters(prev => prev.map((counter, i) => 
      i === index ? { ...counter, [field]: value } : counter
    ));
  };

  return (
    <>
      {/* Settings Button */}
      <button
        onClick={handleOpen}
        className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors touch-manipulation"
        title="Edit counter settings"
      >
        <Settings className="w-5 h-5" />
      </button>

      {/* Settings Modal */}
      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Day Counter Settings"
        maxWidth="md"
      >
        <div className="space-y-6">
          {editedCounters.map((counter, index) => (
            <div key={index} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              {/* Counter Label */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Counter Name
                </label>
                <input
                  type="text"
                  value={counter.label}
                  onChange={(e) => updateCounter(index, 'label', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base md:text-sm"
                  placeholder="e.g., PTO Days"
                />
              </div>

              {/* Total Days */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Total Days Available
                </label>
                <input
                  type="number"
                  value={counter.total}
                  onChange={(e) => updateCounter(index, 'total', parseInt(e.target.value) || 0)}
                  min="0"
                  max="365"
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg md:text-base font-bold"
                  placeholder="15"
                />
              </div>

              {/* Current Usage (Read-only display) */}
              <div className="p-3 bg-slate-900/50 rounded border border-slate-600">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">Currently Used:</span>
                  <span className="text-lg font-bold text-white">{counter.used} days</span>
                </div>
              </div>
            </div>
          ))}

          {/* Save Button */}
          <button
            onClick={handleSave}
            className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation text-lg md:text-base"
          >
            <Save className="w-5 h-5" />
            Save Settings
          </button>
        </div>
      </ResponsiveModal>
    </>
  );
}
